// Submit handler for the enquiry form on /contact-us/ and /ate/
// (contact-lead-form.html). Wires up Turnstile, checks the basics, POSTs
// to the worker's /contact endpoint (which emails the team and makes a CRM
// lead) and shows the result inline.

import { onReady } from "#public/utils/on-ready.js";

const FORM_SELECTOR = "form[data-contact-lead-form]";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WIDGETS = new Map();

const whenTurnstileReady = () =>
  new Promise((resolve) => {
    if (window.turnstile) return resolve(window.turnstile);
    const start = Date.now();
    const poll = () => {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - start > 10000) return resolve(null);
      setTimeout(poll, 100);
    };
    poll();
  });

const renderTurnstile = async (form) => {
  const placeholder = form.querySelector("[data-contact-lead-turnstile]");
  const sitekey = form.dataset.turnstileKey || "";
  if (!placeholder || !sitekey) return;
  const turnstile = await whenTurnstileReady();
  if (!turnstile) return;
  try {
    const id = turnstile.render(placeholder, {
      sitekey,
      action: "contact",
      theme: "light",
    });
    if (id) WIDGETS.set(form, id);
  } catch (_e) {
    // No-op: render can throw if already mounted.
  }
};

const resetTurnstile = (form) => {
  const id = WIDGETS.get(form);
  if (!window.turnstile || !id) return;
  try {
    window.turnstile.reset(id);
  } catch (_e) {
    // No-op
  }
};

const getValue = (form, name) => {
  const el = form.querySelector(`[name="${name}"]`);
  return el && typeof el.value === "string" ? el.value.trim() : "";
};

const FIELDS = [
  "first_name",
  "last_name",
  "email",
  "mobile",
  "event_date",
  "event_type",
  "venue_postcode",
  "max_budget",
  "message",
  "website",
];

const buildPayload = (form) => ({
  ...Object.fromEntries(FIELDS.map((name) => [name, getValue(form, name)])),
  form: form.dataset.form === "ate" ? "ate" : "contact",
  turnstile_token: getValue(form, "cf-turnstile-response"),
});

// /ate/ has no email box, so it needs the mobile instead.
const validate = (form, payload) => {
  if (!payload.first_name || !payload.last_name) {
    return "Please add your first and last name.";
  }
  const hasEmailBox = Boolean(form.querySelector("[name='email']"));
  if (hasEmailBox && !EMAIL_RE.test(payload.email)) {
    return "Please add a valid email address.";
  }
  if (!hasEmailBox && !payload.mobile) return "Please add your mobile number.";
  if (!payload.turnstile_token) {
    return "Please wait for the security check to finish, then submit again.";
  }
  return null;
};

const showMessage = (form, kind, text) => {
  const msg = form.querySelector("[data-contact-lead-msg]");
  if (!msg) return;
  msg.hidden = false;
  msg.classList.remove("lb-form__msg--success", "lb-form__msg--error");
  msg.classList.add(`lb-form__msg--${kind}`);
  msg.textContent = text;
};

const setSubmitting = (form, isSubmitting) => {
  const btn = form.querySelector("[data-contact-lead-submit]");
  if (!btn) return;
  if (!btn.dataset.label) btn.dataset.label = btn.textContent;
  btn.disabled = isSubmitting;
  btn.textContent = isSubmitting ? "Sending…" : btn.dataset.label;
};

const post = async (workerUrl, payload) => {
  try {
    const res = await fetch(`${workerUrl.replace(/\/$/, "")}/contact`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch (_err) {
    return false;
  }
};

const submitForm = async (form) => {
  const workerUrl = (form.dataset.workerUrl || "").trim();
  if (!workerUrl) {
    showMessage(
      form,
      "error",
      "Form isn't configured — please call us on 03331 126993.",
    );
    return;
  }
  const payload = buildPayload(form);
  const error = validate(form, payload);
  if (error) {
    showMessage(form, "error", error);
    return;
  }
  setSubmitting(form, true);
  const ok = await post(workerUrl, payload);
  setSubmitting(form, false);
  resetTurnstile(form);
  if (!ok) {
    showMessage(
      form,
      "error",
      "Sorry, something went wrong sending your enquiry. Please try again or call us on 03331 126993.",
    );
    return;
  }
  form.reset();
  showMessage(
    form,
    "success",
    "Thank you, your form has been submitted. We'll be in touch soon.",
  );
};

onReady(() => {
  for (const form of document.querySelectorAll(FORM_SELECTOR)) {
    renderTurnstile(form);
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      submitForm(form);
    });
  }
});
